import { PixelsJS } from '../../types/pixelsjs';
import videojs from 'video.js';
import { elementViewableManager } from '../../services/horizontal-dom';
import { metrics } from '../../services/requests/metrics';

const { pixelsjs } = window;
const Plugin: typeof videojs.Plugin = pixelsjs.getPlugin('plugin');

class PixelsMetrics extends Plugin {
  custom10 = {};
  currentPlayer: PixelsJS;
  opts: PixelsMetricsOptions;

  constructor(player: PixelsJS, options: PixelsMetricsOptions) {
    super(player);
    this.opts = options
    this._sendEmbed();
    this.currentPlayer = player;
    this.watchVideoViewable();
  }

  public async attachPlayerEventListener() {
    this._initialPlaying().then(() => {
      this._attachEventToMux('player_muted', this.player.muted());
      this._attachEventToMux('player_autoplay', this.player.autoplay());
    });
    this._adRequest().then(() => {
      this.currentPlayer.mux.triggerAdRequest();
      this._attachEventToMux('ads_requested', true);
    });
  }


  public async attachAdsEventListener() {
    this._waitForImaEvent(google.ima.AdEvent.Type.IMPRESSION)
      .then(() => this._attachEventToMux('ads_impression', true));
    this._waitForImaEvent(google.ima.AdEvent.Type.COMPLETE)
      .then(() => this._attachEventToMux('ads_completed', true));

    // only start when the ad's in progress
    this._waitForImaEvent(google.ima.AdEvent.Type.IMPRESSION)
      .then(() => this.watchViewable())
      .then(() => this._attachEventToMux('ads_viewable', true));
  }

  public async watchVideoViewable() {
    const viewable = elementViewableManager(this.player.el(), 2000);

    this.currentPlayer.one('playing', () => {
      viewable.watchImpression(() => {
        this._attachEventToMux('video_viewable', true);
        viewable.dispose();
      });
    });

    this.currentPlayer.on('beforeplaylistitem', () => {
      this._resetCustom10();
      this.attachPlayerEventListener();
      this._attachEventToMux('video_viewable', false);
      if (viewable) {
        viewable.dispose();
      }
      this.watchVideoViewable();
    });
  }

  private _resetCustom10() {
    // eslint-disable-next-line no-unsafe-optional-chaining
    const { data: muxMetadata } = this.player.options({}).plugins?.mux;

    if (muxMetadata.custom_10) {
      try {
        this.custom10 = JSON.parse(muxMetadata.custom_10);
      } catch (ex) {
        this.custom10 = {};
      }
    }
  }

  private async watchViewable() {
    return new Promise((resolve) => {
      const viewable = elementViewableManager(this.player.el(), 2000);

      viewable.watchImpression(() => {
        resolve(true);
        viewable.dispose();
      });

      const { COMPLETE, SKIPPED } = google.ima.AdEvent.Type;
      [COMPLETE, SKIPPED].forEach((event) => {
        this._waitForImaEvent(event).then(() => {
          viewable.dispose();
        });
      });
    });
  }

  private async _adRequest() {
    return new Promise((resolve) => {
      this.currentPlayer.one('ads-request', resolve);
    });
  }

  private async _initialPlaying() {
    return new Promise((resolve) => {
      this.currentPlayer.one('playing', resolve);
    });
  }

  private async _waitForImaEvent(type: typeof google.ima.AdEvent.Type) {
    try {
      return new Promise((resolve) => this.currentPlayer.ima.addEventListener(type, resolve));
    } catch (ex) {
      return null;
    }
  }

  _attachEventToMux(key: string, value: string | boolean) {
    this.custom10 = { ...this.custom10, [key]: value };
    this.currentPlayer.mux.emit('hb', {
      custom_10: JSON.stringify(this.custom10)
    });
  }

  _sendEmbed() {
    const { pageId, pageUrl, componentSessionId, designVariant } = this.opts;
    metrics(pageId, pageUrl, componentSessionId, designVariant, false)({ type: 'embed'});
  }
}

pixelsjs.registerPlugin('pixelsMetrics', PixelsMetrics);
